import { ChartBuilder } from '@shared/engine/pulse/chartBuilder';
import { PALETTE_MAGENTA } from '@shared/builtinPulseMaps/palettes';
import { buildBuiltinAudio, selectBeats } from '@shared/builtinPulseMaps/audioFromOnsets';
import onAndOnOnsets from '@shared/builtinPulseMaps/audio/on-and-on.onsets.json' with { type: 'json' };

/**
 * Stripped-down chart used by /tutorial. Reuses the slowest builtin track
 * (On & On at 86 BPM, ~700ms per beat) and only places ~10 pads with
 * generous breathing room so a brand-new player has a chance to read each
 * one. Pads are pulled from the real onset list so the validator's
 * onset-proximity rule still passes; the `minSpacing: 2` floor keeps them
 * comfortably far apart.
 *
 * Tutorial overlay text in `TutorialPage` is keyed off the beat numbers
 * below; if you change the chart length, update the overlays too.
 */
const TUTORIAL_TOTAL_BEATS = 32;

export function buildTutorialChart() {
  const audio = buildBuiltinAudio({
    src: '/audio/tracks/on-and-on.mp3',
    attribution:
      'Tutorial uses "On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
    data: onAndOnOnsets,
  });

  const builder = new ChartBuilder({
    id: 'tutorial',
    slug: 'tutorial',
    title: 'How to play',
    description:
      'Quick teaching chart on a slow track. Tap when a pad lights up; pads always alternate floor and ceiling.',
    bpm: onAndOnOnsets.bpm,
    difficulty: 1,
    variant: 'normal',
    spawnSurface: 'floor',
    palette: PALETTE_MAGENTA,
    audio,
  });

  builder.section({ startBeat: 0, endBeat: 12, kind: 'verse' });
  builder.section({ startBeat: 12, endBeat: TUTORIAL_TOTAL_BEATS, kind: 'chorus' });

  // Sparse intro: every 4th-ish strong onset so the player gets time to
  // read each pad. Then double the cadence in the chorus to introduce the
  // "things speed up at the chorus" pattern they'll see in real charts.
  const introPads = selectBeats(onAndOnOnsets, {
    fromBeat: 4,
    untilBeat: 12,
    minIntensity: 0.3,
    minSpacing: 2,
  }).slice(0, 4);
  const chorusPads = selectBeats(onAndOnOnsets, {
    fromBeat: 12,
    untilBeat: TUTORIAL_TOTAL_BEATS - 2,
    minIntensity: 0.25,
    minSpacing: 1.5,
  }).slice(0, 8);
  for (const beat of [...introPads, ...chorusPads]) {
    builder.pad(beat);
  }

  builder.setTotalBeats(TUTORIAL_TOTAL_BEATS);
  return builder.build();
}

/**
 * Overlay instructions, each anchored to a beat. `untilBeat` defines when
 * the next overlay takes over. Renderer in TutorialPage shows the most
 * recent overlay whose `fromBeat <= currentBeat < untilBeat`.
 */
export interface TutorialOverlay {
  fromBeat: number;
  untilBeat: number;
  title: string;
  body: string;
}

/**
 * Returns the overlay sequence with input wording adapted to the device.
 * Touch devices are told to "tap"; keyboard devices keep the SPACE hint
 * so first-time players know exactly which key triggers the flip.
 */
export function getTutorialOverlays(isTouch: boolean): TutorialOverlay[] {
  return [
    {
      fromBeat: -8,
      untilBeat: 5,
      title: isTouch ? 'Tap to flip gravity' : 'Tap SPACE to flip gravity',
      body: 'Watch for glowing pads on the floor and ceiling. Tap when one reaches you.',
    },
    {
      fromBeat: 5,
      untilBeat: 13,
      title: 'Pads always alternate',
      body: 'Floor → ceiling → floor → ceiling. The shrinking ring shows when to tap.',
    },
    {
      fromBeat: 13,
      untilBeat: 23,
      title: 'Don\u2019t tap on empty space',
      body: 'Empty taps cost HP. Misses cost more. Tap when you hear a hit and a pad is lined up.',
    },
    {
      fromBeat: 23,
      untilBeat: 100,
      title: 'You\u2019ve got it',
      body: 'Cleared! Pick a real track from the home screen when you\u2019re ready.',
    },
  ];
}
